import { useEffect, useState } from "react";
import api from "@/modules/growing-circle/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, X, RefreshCw, Copy } from "lucide-react";

const STATUS_STYLE = {
  doubtful: "bg-primary/15 text-primary",
  invalid: "bg-destructive/15 text-destructive",
  rejected: "bg-destructive/15 text-destructive",
  approved: "bg-accent/20 text-accent",
  verified: "bg-accent/20 text-accent",
  needs_new_photo: "bg-primary/15 text-primary",
};
const STATUS_LABEL = {
  doubtful: "Doubtful", invalid: "Invalid", approved: "Approved", verified: "Verified (auto)",
  rejected: "Rejected", needs_new_photo: "New photo requested",
};

export default function AdminVerifications() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [images, setImages] = useState({});

  const load = () => api.get("/admin/verifications").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const loadImage = async (id) => {
    try {
      const { data } = await api.get(`/admin/verifications/${id}/image`);
      setImages((m) => ({ ...m, [id]: data.image_base64 }));
    } catch { toast.error("Image no longer available."); }
  };

  const act = async (id, action) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/verifications/${id}/${action}`);
      if (action === "approve" && data.activation_link) {
        toast.success("Approved. Activation link copied (email is in simulated mode).");
        navigator.clipboard?.writeText(data.activation_link).catch(() => {});
      } else {
        toast.success("Done.");
      }
      load();
    } catch (e) {
      toast.error("Error. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <h1 className="font-heading text-3xl tracking-tight mb-2">Access requests</h1>
      <p className="text-muted-foreground mb-8 text-sm">
        Doubtful requests await your manual review. Images are deleted after the decision.
      </p>

      {items.length === 0 ? (
        <p className="text-muted-foreground">No requests at the moment.</p>
      ) : (
        <div className="space-y-4" data-testid="verifications-list">
          {items.map((r) => (
            <div key={r.id} className="bg-card border border-border rounded-2xl p-6" data-testid={`verification-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex gap-4">
                  {images[r.id] ? (
                    <img src={`data:image/jpeg;base64,${images[r.id]}`} alt="verification selfie" className="w-24 h-24 object-cover rounded-xl border border-border" />
                  ) : r.status === "doubtful" && !r.image_deleted ? (
                    <button onClick={() => loadImage(r.id)} className="w-24 h-24 rounded-xl bg-secondary flex items-center justify-center text-xs text-primary text-center px-2 hover:bg-secondary/70" data-testid={`view-image-${r.id}`}>View image</button>
                  ) : (
                    <div className="w-24 h-24 rounded-xl bg-secondary flex items-center justify-center text-xs text-muted-foreground text-center px-2">Image deleted</div>
                  )}
                  <div>
                    <p className="font-medium">{r.name}</p>
                    <p className="text-sm text-muted-foreground">{r.email}</p>
                    <p className="text-sm text-muted-foreground">Edition: {r.edition} · Type: {r.verification_type}</p>
                    <span className={`inline-block mt-2 text-xs px-3 py-1 rounded-full ${STATUS_STYLE[r.status] || "bg-muted"}`}>{STATUS_LABEL[r.status] || r.status}</span>
                  </div>
                </div>
              </div>
              {r.vision && (
                <div className="mt-4 text-xs text-muted-foreground bg-secondary/40 rounded-xl p-3">
                  <strong>Automatic analysis:</strong> {r.vision.reasons || "—"}
                  {r.vision.confidence != null && <> · Confidence: {Math.round((r.vision.confidence || 0) * 100)}%</>}
                </div>
              )}
              {r.activation_link && (
                <div className="mt-3 flex items-center gap-2 text-xs bg-accent/10 rounded-xl p-3 break-all">
                  <Copy className="w-3.5 h-3.5 shrink-0" />
                  <span>Activation link (simulated email): {r.activation_link}</span>
                </div>
              )}
              {["doubtful", "invalid", "rejected", "needs_new_photo"].includes(r.status) && (
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button size="sm" className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" disabled={busy === r.id} onClick={() => act(r.id, "approve")} data-testid={`approve-${r.id}`}>
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" disabled={busy === r.id} onClick={() => act(r.id, "request-new")} data-testid={`request-new-${r.id}`}>
                    <RefreshCw className="w-4 h-4 mr-1" /> New photo
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full text-destructive border-destructive/30" disabled={busy === r.id} onClick={() => act(r.id, "reject")} data-testid={`reject-${r.id}`}>
                    <X className="w-4 h-4 mr-1" /> Reject
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
